import React, {useEffect, useState} from "react";
import { Form, Radio, Select } from 'antd';
import GenericDialog from '../../common/GenericDialog';
import TooltipMUI from '@mui/material/Tooltip';
import { HelpIcon } from '../../common/icons';
import axios from "../../common/axios";

const { Option } = Select;

const CopyDiagnosticTestsDialog = ({
                            open,
                            onClose,
                            sharedTest,
                            selectedSingleTest,
                            testGroupData,
                            handleReloadSharedTests,
                            setIsTableLoading,
                            addTestToGroup
                         }) => {
    const SHARE_TEST_ACTION_TYPE = {
        COPY_OR_MOVE: {val:'COPY_OR_MOVE',display:'Copy or move'},
        COPY: {val:'COPY', display:'Copy'},
        MOVE: {val:'MOVE', display:'Move'}
    };

    const [copyTestForm] = Form.useForm(),
        [copyOrMove, setCopyOrMove] = useState(SHARE_TEST_ACTION_TYPE.COPY),
        [copyOrMoveSelected, setCopyOrMoveSelected] = useState(SHARE_TEST_ACTION_TYPE.COPY.val);

    const handleChangeCopyOrMove = ({target: {value}}) => {
        setCopyOrMoveSelected(value);
    }

    const handleCopyTest = async () => {
        const fieldValues = copyTestForm.getFieldsValue(),
            copyTestGroupId = fieldValues.copyTestGroupId,
            questionOrder = fieldValues.questionOrder;

        if (copyTestGroupId === undefined) {
            copyTestForm.validateFields();
            return Promise.reject('Please select test family.');
        }
        return copyTest(selectedSingleTest.testId, copyTestGroupId, questionOrder);
    }

    const copyTest = async (testId, copyTestGroupId, randomiseQuestions) => {
        setIsTableLoading(true);
        await axios.post('/web/ui/test/copyTestIntoTestGroup', null, {
            params: {
                testId: testId,
                testGroupId: parseInt(copyTestGroupId),
                randomiseQuestions: randomiseQuestions,
                copyOrMove: SHARE_TEST_ACTION_TYPE[copyOrMoveSelected].val
            }
        })
            .then(response => {
                const updateTestGroupData = [...testGroupData];
                if (response.data) {
                    updateTestGroupData.forEach((testGroup) => {
                        if (parseInt(testGroup.testGroupId) === parseInt(copyTestGroupId)) {
                            addTestToGroup(testGroup, response.data);
                        }
                    });
                }
            }).catch((error) => {
                console.error('Error copying test: ', error);
            }).finally(() => {
                if (copyOrMoveSelected === SHARE_TEST_ACTION_TYPE.MOVE.val) {
                    handleReloadSharedTests();
                }
                setIsTableLoading(false);
                onClose();
                copyTestForm.resetFields();
            });
    }

    useEffect(() => {
        copyTestForm.resetFields();
    }, [open]);

    useEffect(() => {
        setCopyOrMove(sharedTest ? SHARE_TEST_ACTION_TYPE.COPY_OR_MOVE : SHARE_TEST_ACTION_TYPE.COPY);
    }, [sharedTest]);

    return (
        <GenericDialog
            title={`${copyOrMove.display} '${selectedSingleTest?.name || ''}'`}
            open={open}
            contentStyle={{ width: 420 }}
            onClose={onClose}
            buttons={[
                {
                    name: 'Cancel',
                    extraProps: { type: 'text' },
                },
                {
                    name: SHARE_TEST_ACTION_TYPE[copyOrMoveSelected].display,
                    htmlType: 'submit',
                    callback: handleCopyTest,
                },
            ]}
        >
            <div className="dialog-form-tooltip-wrapper" style={{ left: 175, top: 6 }}>
                <TooltipMUI placement="top" title={<div className={'tooltip-wrapper'}>
                    <p>Choose the same order of questions for the cloned test or randomise a new order.</p>
                </div>}>
          <span>
            <HelpIcon
                sx={{ m: 1 }}
                style={{ verticalAlign: 'middle', width: '16px', height: '16px' }}
            />
          </span>
                </TooltipMUI>
            </div>

            <Form
                className="testFamilyForm"
                form={copyTestForm}
                labelCol={{ span: 11 }}
                wrapperCol={{ span: 16 }}
                initialValues={{ questionOrder: false }}
            >
                {/* Question order */}
                <Form.Item label="Question order" name="questionOrder">
                    <Radio.Group
                        buttonStyle={'solid'}
                        options={[
                            { label: 'Keep', value: false },
                            { label: 'Randomise', value: true },
                        ]}
                        optionType="button"
                        disabled={SHARE_TEST_ACTION_TYPE[copyOrMoveSelected].val === SHARE_TEST_ACTION_TYPE.MOVE.val}
                    />
                </Form.Item>

                {copyOrMove.val === SHARE_TEST_ACTION_TYPE.COPY_OR_MOVE.val && (
                    <Form.Item
                        label={SHARE_TEST_ACTION_TYPE.COPY_OR_MOVE.display}
                        name="copyOrMove"
                        rules={[
                            {
                                required: copyOrMove.val === SHARE_TEST_ACTION_TYPE.COPY_OR_MOVE.val,
                                message: 'Please select to copy or move.',
                            },
                        ]}
                    >
                        <Radio.Group
                            buttonStyle={'solid'}
                            options={[
                                { label: 'Copy', value: SHARE_TEST_ACTION_TYPE.COPY.val },
                                { label: 'Move', value: SHARE_TEST_ACTION_TYPE.MOVE.val },
                            ]}
                            onChange={handleChangeCopyOrMove}
                            optionType="button"
                        />
                    </Form.Item>
                )}

                <Form.Item
                    label={`${SHARE_TEST_ACTION_TYPE[copyOrMoveSelected].display} into test family`}
                    name="copyTestGroupId"
                    rules={[
                        {
                            required: true,
                            message: 'Please select test family.',
                        },
                    ]}
                >
                    <Select virtual={false} autoClearSearchValue>
                        {testGroupData.map((group) => (
                            <Option key={group.testGroupId} value={group.testGroupId}>
                                {group.name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            </Form>
        </GenericDialog>
    );
};

export default CopyDiagnosticTestsDialog;
